/** Navigation destinations supplied by either a production or visual-preview wrapper. */
export type KombiProductionCatalogueCategory = 'aiAgents' | 'apps' | 'automations';

export type KombiRouteMap = Readonly<{
  home: string;
  catalogue: Readonly<Record<KombiProductionCatalogueCategory, string>>;
  community: string;
  learning: string;
  profile: string;
  dashboard: string;
  shareAutomation: string;
  shareAutomationStep: (
    step: 'select' | 'context' | 'review' | 'submitted',
    question?: number,
  ) => string;
  toolCatalogue: string;
  toolDetail: (toolId: string) => string;
  automationDetail: (toolId: string) => string;
  personProfile: (userId: string) => string;
}>;

/** Canonical destinations for production and live Kombi surfaces. */
export const kombiProductionRoutes: KombiRouteMap = {
  home: '/',
  catalogue: {
    aiAgents: '/catalogue/ai-agents',
    apps: '/catalogue/apps',
    automations: '/catalogue/automations',
  },
  community: '/community',
  learning: '/learning',
  profile: '/me',
  dashboard: '/dashboard',
  shareAutomation: '/share',
  /**
   * The four live steps. This USED to be `() => '/share'` — it swallowed both arguments, because the
   * journey was a fixture preview and production had only the legacy overlay at `/share`. Now that the
   * Kombi journey IS `/share`, swallowing them would send every Next, Change and Edit back to Select.
   *
   * `select` is `/share` itself, not `/share/select`, so every existing link to `/share` still lands on
   * the journey's first step.
   */
  shareAutomationStep: (step, question) => {
    const path = step === 'select' ? '/share' : `/share/${step}`;
    return step === 'context' && question !== undefined ? `${path}?question=${question}` : path;
  },
  // The redesigned catalogue has category landing routes rather than a generic legacy `/tools` page.
  toolCatalogue: '/catalogue/automations',
  toolDetail: (toolId) => `/tools/${encodeURIComponent(toolId)}`,
  automationDetail: (toolId) => `/tools/${encodeURIComponent(toolId)}`,
  personProfile: (userId) => `/users/${encodeURIComponent(userId)}`,
};

/** Visual-review destinations. Only `/design/*` wrappers may pass this map into a Kombi surface. */
export const kombiFixturePreviewRoutes: KombiRouteMap = {
  ...kombiProductionRoutes,
  home: '/design/kombi',
  catalogue: {
    aiAgents: '/design/kombi-catalogue/ai-agents',
    apps: '/design/kombi-catalogue/apps',
    automations: '/design/kombi-catalogue/automations',
  },
  toolCatalogue: '/design/kombi-catalogue/automations',
  community: '/design/kombi-community',
  profile: '/design/kombi-profile',
  personProfile: (userId) => `/design/kombi-public-profile/${encodeURIComponent(userId)}`,
  dashboard: '/design/kombi-manager-dashboard',
  shareAutomation: '/design/kombi-automation-share/select',
  shareAutomationStep: (step, question) => {
    const path = `/design/kombi-automation-share/${step}`;
    return step === 'context' && question !== undefined ? `${path}?question=${question}` : path;
  },
  toolDetail: (slug) => `/design/kombi-tool/${encodeURIComponent(slug)}`,
  automationDetail: (slug) => `/design/kombi-automation-detail/${encodeURIComponent(slug)}`,
};

/** Real-data reference journey: redesigned previews where available, live catalogue and homepage data. */
export const kombiLivePreviewRoutes: KombiRouteMap = {
  ...kombiFixturePreviewRoutes,
  home: '/design/kombi-live',
  community: '/design/kombi-community-live',
  profile: '/design/kombi-profile-live',
  personProfile: (userId) => `/design/kombi-public-profile-live/${encodeURIComponent(userId)}`,
  catalogue: {
    aiAgents: '/design/kombi-catalogue-live/ai-agents',
    apps: '/design/kombi-catalogue-live/apps',
    automations: '/design/kombi-catalogue-live/automations',
  },
  toolCatalogue: '/design/kombi-catalogue-live/automations',
  toolDetail: (toolId) => `/design/kombi-automation-detail-live/${encodeURIComponent(toolId)}`,
  automationDetail: (toolId) =>
    `/design/kombi-automation-detail-live/${encodeURIComponent(toolId)}`,
};

/** Stable local IDs for generic visual-review tool pages; never sent to a production endpoint. */
export function kombiFixtureToolSlug(title: string): string {
  return title
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
